import React from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { CreditCard, ShieldCheck } from 'lucide-react';

const SQUARE_SDK_URL_PROD = 'https://web.squarecdn.com/v1/square.js';
const SQUARE_SDK_URL_SANDBOX = 'https://sandbox.web.squarecdn.com/v1/square.js';

// Lazily loads the Square Web Payments SDK by injecting a <script> tag.
// Cached per environment so a session that flips between sandbox/prod
// (only happens in dev/QA) loads the correct script.
const _squareSdkPromises = {};
function loadSquareSdk(env) {
  if (window.Square) return Promise.resolve(window.Square);
  const cached = _squareSdkPromises[env];
  if (cached) return cached;
  const src = env === 'sandbox' ? SQUARE_SDK_URL_SANDBOX : SQUARE_SDK_URL_PROD;
  const p = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Square));
      existing.addEventListener('error', () => reject(new Error('Square SDK failed to load')));
      if (window.Square) resolve(window.Square);
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => {
      if (window.Square) resolve(window.Square);
      else reject(new Error('Square SDK loaded but window.Square not present'));
    };
    s.onerror = () => reject(new Error('Square SDK failed to load'));
    document.head.appendChild(s);
  });
  _squareSdkPromises[env] = p;
  return p;
}

/**
 * SquareCardForm — Card + Apple Pay + Google Pay via Square Web Payments SDK.
 *
 * Props:
 *   - applicationId, locationId, environment
 *   - amount (number, USD)
 *   - buyerEmail
 *   - disabled
 *   - onBeforeTokenize?: () => boolean       // SYNC validator (Apple Pay needs sync gesture)
 *   - onPaymentToken: (token, verificationToken?) => Promise<void>
 */
const SquareCardForm = ({
  applicationId,
  locationId,
  environment = 'production',
  amount,
  buyerEmail,
  disabled = false,
  onBeforeTokenize,
  onPaymentToken,
}) => {
  const containerRef = React.useRef(null);
  const googlePayButtonRef = React.useRef(null);
  const applePayButtonRef = React.useRef(null);
  const cardRef = React.useRef(null);
  const paymentsRef = React.useRef(null);
  const googlePayRef = React.useRef(null);
  const applePayRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);
  const [initError, setInitError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [googlePayAvailable, setGooglePayAvailable] = React.useState(false);
  const [applePayAvailable, setApplePayAvailable] = React.useState(false);
  const mountedRef = React.useRef(true);

  // Stable refs for the latest amount / handlers so wallet click listeners
  // (bound once) always see the current values.
  const amountRef = React.useRef(amount);
  React.useEffect(() => { amountRef.current = amount; }, [amount]);
  const onBeforeTokenizeRef = React.useRef(onBeforeTokenize);
  React.useEffect(() => { onBeforeTokenizeRef.current = onBeforeTokenize; }, [onBeforeTokenize]);
  const onPaymentTokenRef = React.useRef(onPaymentToken);
  React.useEffect(() => { onPaymentTokenRef.current = onPaymentToken; }, [onPaymentToken]);

  // Build a Square paymentRequest from the current amount. Used by both
  // Apple Pay and Google Pay. The "total" must be a string in dollars.
  const buildPaymentRequest = (payments, amt) => payments.paymentRequest({
    countryCode: 'US',
    currencyCode: 'USD',
    total: {
      amount: Number(amt || 0).toFixed(2),
      label: 'Total',
    },
  });

  // Wraps an async post-tokenize flow so digital-wallet tokenize() can run
  // synchronously inside the user gesture but we still await the backend.
  const runPaymentTokenFlow = async (tokenResult, paymentsInstance) => {
    if (tokenResult.status !== 'OK') {
      const msg = tokenResult.errors?.[0]?.message || 'Payment was cancelled or failed.';
      // "USER_CANCELED" isn't surfaced as a regular error — most cancels just
      // come back with no token; suppress noisy toasts for those.
      if (tokenResult.errors?.length) toast.error(msg);
      return;
    }
    setSubmitting(true);
    try {
      // Optional buyer verification (3DS/SCA). Most wallet tokens skip it.
      let verificationToken = null;
      try {
        if (paymentsInstance && tokenResult.token) {
          const vr = await paymentsInstance.verifyBuyer(tokenResult.token, {
            amount: Number(amountRef.current || 0).toFixed(2),
            billingContact: buyerEmail ? { email: buyerEmail } : undefined,
            currencyCode: 'USD',
            intent: 'CHARGE',
            customerInitiated: true,
            sellerKeyedIn: false,
          });
          verificationToken = vr?.token || null;
        }
      } catch (vErr) {
        console.warn('Square verifyBuyer failed (continuing):', vErr);
      }
      await onPaymentTokenRef.current(tokenResult.token, verificationToken);
    } catch (e) {
      console.error('Square payment flow failed:', e);
      toast.error(e?.message || 'Payment failed. Please try again.');
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  React.useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    const init = async () => {
      try {
        if (!applicationId || !locationId) {
          throw new Error('Square credentials not configured');
        }
        const Square = await loadSquareSdk(environment);
        if (cancelled) return;
        const payments = Square.payments(applicationId, locationId);
        paymentsRef.current = payments;

        // ---- Card ----
        const card = await payments.card();
        if (cancelled) { try { await card.destroy(); } catch (_) {} return; }
        await card.attach(containerRef.current);
        cardRef.current = card;

        // ---- Google Pay ----
        try {
          const pr = buildPaymentRequest(payments, amountRef.current);
          const googlePay = await payments.googlePay(pr);
          if (cancelled) { try { await googlePay.destroy?.(); } catch (_) {} }
          else if (googlePayButtonRef.current) {
            await googlePay.attach(googlePayButtonRef.current, {
              buttonColor: 'black',
              buttonType: 'pay',
              buttonSizeMode: 'fill',
            });
            googlePayRef.current = googlePay;
            // Tokenize on click — Square renders the actual button into the
            // attached element, but our listener still fires before they
            // open their sheet so we can run validation synchronously.
            googlePayButtonRef.current.addEventListener('click', async (e) => {
              const validator = onBeforeTokenizeRef.current;
              if (validator && validator() === false) {
                e.preventDefault();
                return;
              }
              try {
                const tokenResult = await googlePay.tokenize();
                await runPaymentTokenFlow(tokenResult, paymentsRef.current);
              } catch (err) {
                console.error('Google Pay tokenize failed:', err);
                toast.error('Google Pay failed. Please try a different method.');
              }
            });
            if (mountedRef.current) setGooglePayAvailable(true);
          }
        } catch (gErr) {
          // Google Pay not available (e.g. unsupported browser). Silent.
          console.info('Google Pay unavailable:', gErr?.message || gErr);
        }

        // ---- Apple Pay ----
        try {
          const pr2 = buildPaymentRequest(payments, amountRef.current);
          const applePay = await payments.applePay(pr2);
          if (!cancelled && applePayButtonRef.current) {
            applePayRef.current = applePay;
            // Apple Pay tokenize() MUST be called synchronously inside the
            // click handler. So we run sync validation, then tokenize, then
            // hand off to the async flow.
            applePayButtonRef.current.addEventListener('click', async (e) => {
              e.preventDefault();
              const validator = onBeforeTokenizeRef.current;
              if (validator && validator() === false) {
                return;
              }
              try {
                const tokenResult = await applePay.tokenize();
                await runPaymentTokenFlow(tokenResult, paymentsRef.current);
              } catch (err) {
                console.error('Apple Pay tokenize failed:', err);
                toast.error('Apple Pay failed. Please try a different method.');
              }
            });
            if (mountedRef.current) setApplePayAvailable(true);
          }
        } catch (aErr) {
          // Apple Pay unavailable: wrong browser/device, or domain not
          // verified with Square. Silent; the user still gets the card form.
          console.info('Apple Pay unavailable:', aErr?.message || aErr);
        }

        if (!cancelled && mountedRef.current) setReady(true);
      } catch (e) {
        console.error('Square init failed:', e);
        if (!cancelled && mountedRef.current) {
          setInitError(e?.message || 'Failed to load payment form');
        }
      }
    };
    init();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      try { cardRef.current?.destroy?.(); } catch (_) {}
      try { googlePayRef.current?.destroy?.(); } catch (_) {}
      try { applePayRef.current?.destroy?.(); } catch (_) {}
      cardRef.current = null;
      googlePayRef.current = null;
      applePayRef.current = null;
      paymentsRef.current = null;
    };
    // We only initialize the SDK once. amount updates are read via
    // amountRef inside the wallet click handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCardPay = async () => {
    if (submitting || disabled || !ready || !cardRef.current) return;
    const validator = onBeforeTokenizeRef.current;
    if (validator && validator() === false) return;
    setSubmitting(true);
    try {
      const verificationDetails = {
        amount: Number(amountRef.current || 0).toFixed(2),
        billingContact: buyerEmail ? { email: buyerEmail } : undefined,
        currencyCode: 'USD',
        intent: 'CHARGE',
        customerInitiated: true,
        sellerKeyedIn: false,
      };
      const tokenResult = await cardRef.current.tokenize(verificationDetails);
      if (tokenResult.status !== 'OK') {
        const msg = tokenResult.errors?.[0]?.message || 'Card validation failed. Please review and try again.';
        toast.error(msg);
        setSubmitting(false);
        return;
      }
      let verificationToken = null;
      try {
        if (paymentsRef.current && tokenResult.token) {
          const vr = await paymentsRef.current.verifyBuyer(tokenResult.token, verificationDetails);
          verificationToken = vr?.token || null;
        }
      } catch (vErr) {
        console.warn('Square verifyBuyer failed (continuing):', vErr);
      }
      await onPaymentTokenRef.current(tokenResult.token, verificationToken);
    } catch (e) {
      console.error('Square pay failed:', e);
      toast.error(e?.message || 'Payment failed. Please try again.');
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  if (initError) {
    return (
      <div
        className="rounded-xl border border-red-300 bg-red-50 p-4 text-center"
        data-testid="square-init-error"
      >
        <p className="text-sm font-semibold text-red-900">Payment form unavailable</p>
        <p className="text-xs text-red-700 mt-1">{initError}</p>
        <p className="text-xs text-red-700 mt-2">Please refresh and try again, or contact support.</p>
      </div>
    );
  }

  const showWalletsDivider = googlePayAvailable || applePayAvailable;

  return (
    <div className="space-y-3" data-testid="square-card-form">
      {/* Digital wallets — render above the card form. The ref-holding divs
          must exist in the DOM BEFORE the SDK init effect runs (otherwise
          refs are null and attach() never fires), so we render them
          unconditionally and just hide them via CSS when the wallet is
          unavailable in this browser. */}
      <div className="space-y-2" data-testid="square-wallets">
        <div
          ref={applePayButtonRef}
          id="apple-pay-button"
          className="w-full h-12 rounded-xl overflow-hidden cursor-pointer apple-pay-button apple-pay-button-black"
          style={{
            WebkitAppearance: '-apple-pay-button',
            appearance: '-apple-pay-button',
            ApplePayButtonType: 'pay',
            ApplePayButtonStyle: 'black',
            display: applePayAvailable ? 'block' : 'none',
          }}
          data-testid="apple-pay-button"
          role="button"
          aria-label="Pay with Apple Pay"
        />
        <div
          ref={googlePayButtonRef}
          id="google-pay-button"
          className="w-full h-12 rounded-xl overflow-hidden"
          style={{ display: googlePayAvailable ? 'block' : 'none' }}
          data-testid="google-pay-button"
        />
        {showWalletsDivider && (
          <div className="flex items-center gap-3 my-2" data-testid="square-wallets-divider">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs uppercase tracking-wide text-slate-400">or pay by card</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
        )}
      </div>

      <div
        ref={containerRef}
        id="square-card-container"
        className="min-h-[100px] rounded-xl border border-slate-200 bg-white p-3"
        data-testid="square-card-container"
      />
      {!ready && (
        <div className="flex items-center justify-center gap-2 py-2 text-xs text-slate-500">
          <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-teal-700"></span>
          Loading secure payment form...
        </div>
      )}
      <Button
        type="button"
        onClick={handleCardPay}
        disabled={disabled || !ready || submitting}
        className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white disabled:bg-slate-300"
        data-testid="square-pay-button"
      >
        {submitting ? (
          <span className="flex items-center gap-2">
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
            Processing...
          </span>
        ) : (
          <>
            <CreditCard className="mr-2 h-4 w-4" />
            Pay ${Number(amount || 0).toFixed(2)}
          </>
        )}
      </Button>
      <p className="flex items-center justify-center gap-1 text-xs text-slate-400">
        <ShieldCheck className="h-3 w-3" />
        Secure payment powered by Square
      </p>
    </div>
  );
};

export default SquareCardForm;
